import React, { useEffect, useRef, useState } from "react";
import { MapPin, AlertCircle, Loader2 } from "lucide-react";

// City coordinates for Indian cities (approximate centers)
const CITY_COORDINATES = {
  "Mumbai": { lat: 19.0760, lng: 72.8777 },
  "Delhi": { lat: 28.6139, lng: 77.2090 },
  "Bangalore": { lat: 12.9716, lng: 77.5946 },
  "Chennai": { lat: 13.0827, lng: 80.2707 },
  "Kolkata": { lat: 22.5726, lng: 88.3639 },
  "Hyderabad": { lat: 17.3850, lng: 78.4867 },
  "Pune": { lat: 18.5204, lng: 73.8567 },
  "Ahmedabad": { lat: 23.0225, lng: 72.5714 },
  "Jaipur": { lat: 26.9124, lng: 75.7873 },
  "Surat": { lat: 21.1702, lng: 72.8311 },
  "Lucknow": { lat: 26.8467, lng: 80.9462 },
  "Kanpur": { lat: 26.4499, lng: 80.3319 },
  "Nagpur": { lat: 21.1458, lng: 79.0882 },
  "Indore": { lat: 22.7196, lng: 75.8577 },
  "Thane": { lat: 19.2183, lng: 72.9781 },
  "Bhopal": { lat: 23.2599, lng: 77.4126 },
  "Goa": { lat: 15.2993, lng: 74.1240 },
  "Chandigarh": { lat: 30.7333, lng: 76.7794 },
};

export function RouteMap({ listing }) {
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);

  useEffect(() => {
    // Load Google Maps script if not already loaded
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        setMapLoaded(true);
        return;
      }

      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        setMapError("Google Maps API key not configured");
        return;
      }

      const existingScript = document.getElementById('google-maps-script');
      if (existingScript) {
        existingScript.addEventListener('load', () => setMapLoaded(true));
        return;
      }

      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => setMapLoaded(true);
      script.onerror = () => setMapError("Failed to load Google Maps");
      document.head.appendChild(script);
    };

    loadGoogleMaps();
  }, []);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !listing) return;

    const origin = CITY_COORDINATES[listing.origin_city];
    const destination = CITY_COORDINATES[listing.destination_city];

    if (!origin || !destination) {
      setMapError(`Location not found: ${!origin ? listing.origin_city : listing.destination_city}`);
      return;
    }

    try {
      // Initialize map
      const map = new window.google.maps.Map(mapRef.current, {
        zoom: 6,
        center: {
          lat: (origin.lat + destination.lat) / 2,
          lng: (origin.lng + destination.lng) / 2,
        },
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          { elementType: "geometry", stylers: [{ color: "#1a1a2e" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#1a1a2e" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#8a8a8a" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#38383d" }] },
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e4a6e" }] },
        ],
      });

      // Initialize directions renderer
      const renderer = new window.google.maps.DirectionsRenderer({
        map: map,
        suppressMarkers: false,
        polylineOptions: {
          strokeColor: "#f97316",
          strokeOpacity: 0.8,
          strokeWeight: 4,
        },
      });
      setDirectionsRenderer(renderer);

      // Calculate route
      const directionsService = new window.google.maps.DirectionsService();
      
      directionsService.route(
        {
          origin: origin,
          destination: destination,
          travelMode: getTravelMode(listing.transport_mode),
        },
        (result, status) => {
          if (status === "OK") {
            renderer.setDirections(result);
          } else {
            // Fallback: draw straight line if directions fail
            drawStraightLine(map, origin, destination);
          }
        }
      );
    } catch (err) {
      setMapError("Error initializing map");
    }
  }, [mapLoaded, listing]);

  const getTravelMode = (mode) => {
    switch (mode?.toLowerCase()) {
      case "road":
        return window.google.maps.TravelMode.DRIVING;
      case "rail":
        return window.google.maps.TravelMode.TRANSIT;
      default:
        return window.google.maps.TravelMode.DRIVING;
    }
  };

  const drawStraightLine = (map, origin, destination) => {
    // Add markers
    new window.google.maps.Marker({
      position: origin,
      map: map,
      label: "A",
      title: listing.origin_city,
    });

    new window.google.maps.Marker({
      position: destination,
      map: map,
      label: "B",
      title: listing.destination_city,
    });

    // Draw line
    new window.google.maps.Polyline({
      path: [origin, destination],
      geodesic: true,
      strokeColor: "#f97316",
      strokeOpacity: 0.8,
      strokeWeight: 3,
      map: map,
    });
  };

  if (mapError) {
    return (
      <div className="border border-border p-6">
        <h3 className="label-text mb-4">Route Map</h3>
        <div className="aspect-video bg-secondary/30 flex items-center justify-center border border-dashed border-border">
          <div className="text-center text-muted-foreground">
            <AlertCircle className="w-10 h-10 mx-auto mb-2 text-destructive opacity-70" />
            <p className="text-sm">{mapError}</p>
            <p className="text-xs mt-1">{listing.origin_city} → {listing.destination_city}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-border p-6">
      <h3 className="label-text mb-4">Route Map</h3>
      <div className="aspect-video relative">
        {!mapLoaded && (
          <div className="absolute inset-0 bg-secondary/30 flex items-center justify-center border border-dashed border-border">
            <div className="text-center text-muted-foreground">
              <Loader2 className="w-10 h-10 mx-auto mb-2 animate-spin" />
              <p className="text-sm">Loading map...</p>
            </div>
          </div>
        )}
        <div 
          ref={mapRef} 
          className="w-full h-full rounded-sm"
          style={{ minHeight: '300px' }}
          data-testid="route-map"
        />
      </div>
      <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
        <span className="flex items-center gap-1">
          <MapPin className="w-4 h-4 text-green-500" />
          {listing.origin_city}
        </span>
        <span className="text-xs">→</span>
        <span className="flex items-center gap-1">
          <MapPin className="w-4 h-4 text-red-500" />
          {listing.destination_city}
        </span>
      </div>
    </div>
  );
}

export function CargoSpecifications({ listing }) {
  return (
    <div className="border border-border p-6 space-y-4">
      <div>
        <p className="label-text mb-1">Vehicle Type</p>
        <p className="font-bold capitalize">{listing.vehicle_type}</p>
      </div>
      
      {/* Space Dimensions */}
      {(listing.space_length || listing.space_width || listing.space_height || listing.max_weight) && (
        <div className="pt-4 border-t border-border">
          <p className="label-text mb-3">Space Specifications</p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {listing.space_length && listing.space_width && listing.space_height && (
              <div>
                <span className="text-muted-foreground">Dimensions:</span>
                <span className="ml-2 font-medium">{listing.space_length} × {listing.space_width} × {listing.space_height} cm</span>
              </div>
            )}
            {listing.max_weight && (
              <div>
                <span className="text-muted-foreground">Max Weight:</span>
                <span className="ml-2 font-medium">{listing.max_weight} kg</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ETA */}
      {listing.eta_hours && (
        <div className="pt-4 border-t border-border">
          <p className="label-text mb-1">Estimated Time of Arrival</p>
          <p className="font-bold">{listing.eta_hours} hours</p>
        </div>
      )}
      
      {listing.description && (
        <div className="pt-4 border-t border-border">
          <p className="label-text mb-1">Description</p>
          <p className="text-muted-foreground">{listing.description}</p>
        </div>
      )}
    </div>
  );
}
